import { ConfigRecord, DeviceTypes, ChemistryEdit, ConfigRecordFileNames, SanitizerTypes, HardwareConfig } from "../config";
import { State, findAllStates, ChemistryEngineStates } from "../status";
import Locale from "../utils/Locale";
import { CoreParams } from "../types";
import { MissingCoreParamsError, Json } from "../utils";
import { Chemistry } from "../utils";
import { ChemicalType, ChemicalTypeSalt } from "../types"
import { safeJsonParse } from "../utils/Json";

export * as DesiredStateManager from "./desired-state-manager";

/**
 * Controller enums for BOW state: WaterQualityGraphItem
 */
enum WaterQualityGraphItem {
    AUTOFILL = "AUTOFILL",
    DISSOLVED_OXYGEN = "DISSOLVED_OXYGEN",
    FLOW_METER = "FLOW_METER",
    FLOW_SWITCH_PROCEDURE = "FLOW_SWITCH_PROCEDURE",
    FREE_CHLORINE = "FREE_CHLORINE",
    ORP = "ORP",
    PH = "PH",
    PRESSURE = "PRESSURE",
    SALT = "SALT",
    TOTAL_CHLORINE = "TOTAL_CHLORINE",
}

export function getWaterQualityFeatures(coreParams: CoreParams, bowUuid?: string ): {
    enablePh: boolean,
    enableOrp: boolean,
    enableSalt: boolean,
    enablePressure: boolean,
    enableRefill: boolean,
    enableFlowCheck: boolean,
    enableFreeChlorine: boolean,
    enableFlowMeter: boolean,
    enableTotalChlorine: boolean,
    enableDissolvedOxygenConcentration: boolean,
    enableDissolvedOxygenSaturation: boolean,
} {
    const configData = coreParams.configs?.find(item => item.fileName === ConfigRecordFileNames.HardwareConfig)?.data;
    const bodyOfWaterStates = [];
    configData?.BodiesOfWater?.forEach(item => {
        bodyOfWaterStates.push({
            states: findAllStates(item.UUID, coreParams.states),
            uuid: item.UUID
        });
    });

    let enablePh = false;
    let enableOrp = false;
    let enableSalt = false;
    let enablePressure = false;
    let enableRefill = false;
    let enableFlowCheck = false;
    let enableFreeChlorine = false;
    let enableTotalChlorine = false;
    let enableFlowMeter = false;
    let enableDissolvedOxygenConcentration = false;
    let enableDissolvedOxygenSaturation = false;

    if (bodyOfWaterStates.some(item => (!bowUuid || item.uuid === bowUuid) &&
        Object.keys(item.states).includes("WaterQualityGraphItems"))
    ) {
        bodyOfWaterStates.filter(item => (!bowUuid || item.uuid === bowUuid))
            .forEach(itemBodyOfWaterStates => {
                const WaterQualityGraphItems = (JSON.parse(itemBodyOfWaterStates.states.WaterQualityGraphItems || "[]") || []) as WaterQualityGraphItem[];
                WaterQualityGraphItems?.forEach(item => {
                    switch (item) {
                    case WaterQualityGraphItem.AUTOFILL: enableRefill = true; break;
                    case WaterQualityGraphItem.DISSOLVED_OXYGEN:
                        enableDissolvedOxygenConcentration = true;
                        enableDissolvedOxygenSaturation = true;
                    break;
                    case WaterQualityGraphItem.FLOW_METER: enableFlowMeter = true; break;
                    case WaterQualityGraphItem.FLOW_SWITCH_PROCEDURE: enableFlowCheck = true; break;
                    case WaterQualityGraphItem.FREE_CHLORINE: enableFreeChlorine = true; break;
                    case WaterQualityGraphItem.ORP: enableOrp = true; break;
                    case WaterQualityGraphItem.PH: enablePh = true; break;
                    case WaterQualityGraphItem.PRESSURE: enablePressure = true; break;
                    case WaterQualityGraphItem.SALT: enableSalt = true; break;
                    case WaterQualityGraphItem.TOTAL_CHLORINE: enableTotalChlorine = true; break;
                    }
                });
            })
        ;
    } else {
        const chemistryEditsRaw = coreParams.siteStates?.ChemistryEdits
        const chemistryEdits = JSON.parse(chemistryEditsRaw || "[]") as ChemistryEdit[];
        
        enablePh = chemistryEdits?.some(item => (!bowUuid || item.BodyOfWaterUUID === bowUuid) && (item.PhProbe));

        enableOrp = chemistryEdits?.some(item =>
            (!bowUuid || item.BodyOfWaterUUID === bowUuid)
            && (
                // Backwards compatibility
                item.ORPProbe
                || item.AvailableSanitizerTargetTypes?.includes(SanitizerTypes.ORP)
            )
        );
        

        enableSalt = chemistryEdits?.some(item => (!bowUuid || item.BodyOfWaterUUID === bowUuid) && item.SaltChlorine);

        enablePressure = configData?.Devices?.some(item => item.DeviceType === DeviceTypes.PRESSURESENSOR);

        enableRefill = bodyOfWaterStates.some(item => {
            if (bowUuid && item.uuid !== bowUuid) return;
            const autoFillEditSettings = JSON.parse(item.states?.AutoFillEditSettings || "{}");
            return autoFillEditSettings?.Enabled
        });

        enableFlowCheck = bodyOfWaterStates.some(itemBodyOfWaterStates => {
            if (bowUuid && itemBodyOfWaterStates.uuid !== bowUuid) return;
            const flowCheckEventSettings = JSON.parse(itemBodyOfWaterStates.FlowCheckEventSettings || "{}");
            return flowCheckEventSettings?.FlowCheckSettingsByPump?.some(item => item.SupportsFlowCheckEvents)
        });
        
        enableFreeChlorine = false;
        enableTotalChlorine = false;

        if (coreParams.siteStates?.AutoFilterVersion >= 32) {
            const availableSanitizerTargetTypes = [];
            chemistryEdits?.forEach(itemChemistryEdit => {
                if (bowUuid && itemChemistryEdit.BodyOfWaterUUID !== bowUuid) return;
                itemChemistryEdit.AvailableSanitizerTargetTypes?.forEach(item => {
                    if (!availableSanitizerTargetTypes.includes(item)) {
                        availableSanitizerTargetTypes.push(item);
                    }
                });
            });
            availableSanitizerTargetTypes.forEach(item => {
                switch (item) {
                case SanitizerTypes.FREE_CHLORINE: enableFreeChlorine = true; break;
                case SanitizerTypes.TOTAL_CHLORINE: enableTotalChlorine = true; break;
                }
            });
        }
    }

    return ({
        enableFlowCheck,
        enableFlowMeter,
        enableFreeChlorine,
        enableOrp,
        enablePh,
        enablePressure,
        enableRefill,
        enableSalt,
        enableTotalChlorine,
        enableDissolvedOxygenConcentration,
        enableDissolvedOxygenSaturation,
    });
}

/**
 * Accepts error object returned from the Controller for a Site.setDeviceState RPC request.
 * Returns:
 * - localized error message and
 * - determines if the error could be temporary or not.
 *  (Worth asking the user to rerty or just show the "please call support" text.)
 * @param error Error got from the Controller
 * @returns
 */
export function processSiteSetDeviceStateError(error: { message: string, code: string }): {
    msgForUser: string,
    retriable: boolean
} { 
    switch(error?.message) {
    /** @todo use error code once Controller supports it */
    case "Not available in current mode":
        return {
            msgForUser: Locale.gettext("Your Attendant has left Installer Mode. Please put it into Installer Mode."),
            retriable: true
        };
    case "Device is offline":
        return {
            msgForUser: Locale.gettext("Your Attendant is offline. Make sure it is turned on, booted up, has an internet connection, and is in Installer Mode."),
            retriable: true,
        }
    default:
        return {
            msgForUser: Locale.gettext('Error sending changes. Please check that the device is powered on and is online.'),
            retriable: false,
        }
    }
}

export function getChemistryEdits(coreParams: CoreParams): ChemistryEdit[] {
    if (!coreParams) throw new MissingCoreParamsError();
    const chemistryEdits = Json.safeJsonParse(
       coreParams.siteStates?.ChemistryEdits || "[]"
    ) ?? [];
    return chemistryEdits;
}

export function getChemistryEdit(coreParams: CoreParams, bowUuid: string): ChemistryEdit {
    if (!coreParams) throw new MissingCoreParamsError();
    const chemistryEdits = getChemistryEdits(coreParams);
    const chemistryEdit = chemistryEdits.find(
        item => item.BodyOfWaterUUID === bowUuid
    ) ?? {} as ChemistryEdit;
    return chemistryEdit;
}

/**
 * Determines whether dosing of at least one type of chemical is available for the given BOW.
 * @param coreParams 
 * @param [bowUuid]
 * @returns 
 * @see isChemistryEditAvailable() in Mobile-App
 */
export const areDosingItemsAvailable = function (coreParams: CoreParams, bowUuid?: string) {
    if (!coreParams) throw new MissingCoreParamsError();
    const chemistryEdits = getChemistryEdits(coreParams);
    const areDosingItemsAvailable = chemistryEdits?.some(item => (
        (!bowUuid || bowUuid === item.BodyOfWaterUUID)
        && (
            item.Acid
            || item?.Base
            || item?.LiquidChlorine
            || item?.CO2
            || item?.SaltChlorine
        )
    ));
    return areDosingItemsAvailable;
}

export function getOneTimeDosingChemicalTypeOptions(coreParams: CoreParams, bodyOfWaterUuid: string) {
    const options = [];
    const chemistryEdit = getChemistryEdit(coreParams, bodyOfWaterUuid);
    if (chemistryEdit.Acid) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.MuriaticAcid),
            value: ChemicalType.MuriaticAcid
        });
    }
    if (chemistryEdit.Base) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.Base),
            value: ChemicalType.Base
        });
    }
    if (chemistryEdit.CO2) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.Co2),
            value: ChemicalType.Co2
        });
    }
    if (chemistryEdit.LiquidChlorine) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.Chlorine),
            value: ChemicalType.Chlorine
        });
    }
    /*
    if (chemistryEdit.SaltChlorine) {
        options.push({
            label: Locale.gettext("Salt"),
            value: ChemicalTypeSalt
        });
    }
    */
    return options;
}

export function getOneTimeDosingChemicalUnits(coreParams: CoreParams, answers) {
    const chemistryEdit = getChemistryEdit(coreParams, answers.bodyOfWaterUuid);

    switch (answers.chemicalType) {
    case ChemicalType.MuriaticAcid:
        return chemistryEdit.AcidDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }));
    case ChemicalType.Base:
        return chemistryEdit.BaseDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    case ChemicalType.Co2:
        return chemistryEdit.CO2DosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    case ChemicalType.Chlorine:
        return chemistryEdit.LiquidChlorineDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    case ChemicalTypeSalt:
        return chemistryEdit.SaltDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    }                
}

export enum ChemistryStateLevel {
    Gray = "gray",
    Red = "red",
    Yellow = "yellow",
    Green = "green",
}

export function getChemistryEngineStateLocalised(state: ChemistryEngineStates): {
    title: string,
    summary?: string,
    description?: string,
    level?: ChemistryStateLevel,
} {
    switch (state) {
    case ChemistryEngineStates.DOSING_OR_MIXING_IN_PROGRESS: return {
        title: Locale.gettext("Mixing In Progress"),
        summary: Locale.gettext("Chemistry adjustments are temporarily paused while chemicals get dispensed, circulate, and mix."),
        description: Locale.gettext("Chemicals were recently added and are still circulating through the water. The system is waiting for them to fully mix before taking another reading and deciding what to do next. There is nothing for you to do at this time - this is normal and expected behavior."),
        level: ChemistryStateLevel.Green
    };
    case ChemistryEngineStates.SUPER_CHLORINATE: return {
        title: Locale.gettext("Super Chlorination Active"),
        summary: Locale.gettext("Super chlorination mode is active and elevated sanitizer levels are being maintained."),
        description: Locale.gettext("Your system is running in super chlorination mode, which intentionally holds a higher-than-normal sanitizer level. This is typically used to clear up water quality issues or after a period of heavy use. There is nothing for you to do at this time - the system will return to normal operation when the super chlorination cycle is complete."),
        level: ChemistryStateLevel.Green
    };
    case ChemistryEngineStates.VALUE_GREATER_THAN_TARGET: return {
        title: Locale.gettext("Above Target Range"),
        summary: Locale.gettext("The measured value is currently above the configured target range."),
        description: Locale.gettext("The measured reading is currently above your configured target. If dosing is enabled, the system will work to keep it in range as needed.") +
            '\n\n' + '• ' + Locale.gettext("If the reading is well above target, run a manual test with a home test kit to verify the sensor reading matches your actual water conditions."),
        level: ChemistryStateLevel.Green
    };
    case ChemistryEngineStates.VALUE_LESS_THAN_TARGET: return {
        title: Locale.gettext("Below Target Range"),
        summary: Locale.gettext("The measured value is currently below the configured target range."),
        description: Locale.gettext("The measured reading is currently below your configured target. If dosing is enabled, the system will work to keep it in range as needed.") +
            '\n\n' + '• ' + Locale.gettext("If the reading is well below target, run a manual test with a home test kit to verify the sensor reading matches your actual water conditions."),
        level: ChemistryStateLevel.Green
    };
    case ChemistryEngineStates.NOT_FILTERING: return {
        title: Locale.gettext("Water Not Filtering"),
        summary: Locale.gettext("Chemistry automation is paused because the water is not currently being filtered."),
        description:   Locale.gettext("The system has paused because water isn't currently moving through the filter. Chemistry automation will resume automatically once filtering starts.") +
            '\n\n' + '• ' + Locale.gettext("Check that your circulation pump is turned on and running.") +
            '\n' + '• ' + Locale.gettext("If the pump is on a schedule, verify the schedule is set correctly and that Away Mode is not active.") +
            '\n' + '• ' + Locale.gettext("If the pump appears to be on but water still isn't moving, check for any error messages on your pump or controller.") +
            '\n' + '• ' + Locale.gettext("Once filtering is confirmed to be running, chemistry automation will resume on its own."),
        level: ChemistryStateLevel.Gray
    };
    case ChemistryEngineStates.SENSOR_MISSING_FROM_ROUTING: return {
        title: Locale.gettext("Sensor Not Routed"),
        summary: Locale.gettext("A required sensor is not included in the current water circulation path."),
        description:   Locale.gettext("The current path of flowing water doesn't include the sensor. Chemistry automation is paused and will resume automatically once water is flowing though the sensor.") +
            '\n\n' + '• ' + Locale.gettext("Check to see that filtering is turned on and running.") +
            '\n' + '• ' + Locale.gettext("Confirm the chemistry sensor is assigned to the correct water circulation path in Installer Mode.") +
            '\n' + '• ' + Locale.gettext("If you recently changed your plumbing or equipment configuration, your Flow settings may need to be updated to reflect those changes.") +
            '\n' + '• ' + Locale.gettext("If you're unsure how to configure your flows, contact your pool professional or Poolside Support."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.DISPENSING_DEVICE_MISSING_FROM_ROUTING: return {
        title: Locale.gettext("Chemical Pump Not Routed"),
        summary: Locale.gettext("A required dispensing device is not included in the current water circulation path."),
        description: Locale.gettext("The chemical pump isn't included in the active water circulation path. Chemistry automated dispensing is paused.") +
            '\n\n' + '• ' + Locale.gettext("Confirm the chemical pump is assigned to the correct water circulation path in Installer Mode.") +
            '\n' + '• ' + Locale.gettext("If you recently changed your plumbing or equipment configuration, your Flow settings may need to be updated to reflect those changes.") +
            '\n' + '• ' + Locale.gettext("If you're unsure how to configure your flows, contact your pool professional or Poolside Support."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.WRONG_TYPE_OF_CHEMICAL_PUMP: return {
        title: Locale.gettext("Incorrect Pump Type"),
        summary: Locale.gettext("The configured chemical pump type does not match the selected chemistry engine."),
        description: Locale.gettext("The type of chemical pump configured in the system doesn't match the dosing method selected. This is a configuration issue that needs to be corrected.") +
            '\n' + '• ' + Locale.gettext("Contact Poolside Support for assistance."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.CHEMICAL_PUMP_BAD_GPD: return {
        title: Locale.gettext("Invalid Pump Capacity"),
        summary: Locale.gettext("The configured pump capacity appears invalid or outside the supported range."),
        description: Locale.gettext("The pump capacity value entered in the system is either missing, set to zero, or outside the supported range. This needs to be corrected before dosing can work.") +
            '\n\n' + '• ' + Locale.gettext("In Installer Mode, open the chemical pump settings.") +
            '\n' + '• ' + Locale.gettext("Locate the \"Flow Rate Output Per Day\" field.") +
            '\n' + '• ' + Locale.gettext("Check the label or documentation for your chemical pump to find the correct daily output rating.  This is usually shown in gallons per day.") +
            '\n' + '• ' + Locale.gettext("Enter the correct value and save your changes. If you're unsure of the correct value, contact your pool professional or Poolside Support."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.DISPENSING_NOT_ENABLED: return {
        title: Locale.gettext("Dosing Disabled"),
        summary: Locale.gettext("Automatic dosing is currently disabled for this chemistry engine."),
        description: Locale.gettext("Automatic dosing has been turned off for this chemistry function. The system will not add any chemicals until it's re-enabled.") +
            '\n\n' + '• ' + Locale.gettext("Open the chemistry settings for the chemical for the specific body of water.") +
            '\n' + '• ' + Locale.gettext("Locate the dosing enable toggle and turn it on.") +
            '\n' + '• ' + Locale.gettext("If dosing was disabled intentionally - for example, during a recent service call — confirm with your pool professional that it's safe to re-enable before doing so."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.WATER_MOVING_PUMP_ERROR: return {
        title: Locale.gettext("Circulation Pump Error"),
        summary: Locale.gettext("Chemistry automation is paused because the circulation pump reported an error."),
        description: Locale.gettext("The circulation pump has reported an error, so chemistry automation has been paused to avoid adding chemicals to standing water.") +
            '\n\n' + '• ' + Locale.gettext("Check your pump controller or display for any error codes or warning lights.") +
            '\n' + '• ' + Locale.gettext("Consult your pump's manual or the manufacturer's documentation for guidance on the specific error shown.") +
            '\n' + '• ' + Locale.gettext("Once the pump error is resolved and water is circulating normally, chemistry automation will resume on its own.") +
            '\n' + '• ' + Locale.gettext("If you can't identify or resolve the pump error, contact your pool professional."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.FLOW_SWITCH_ERROR: return {
        title: Locale.gettext("No Water Flow Detected"),
        summary: Locale.gettext("Chemistry automation is paused because water flow could not be confirmed."),
        description: Locale.gettext("The system couldn't confirm that water is actively flowing, so dosing has been paused as a precaution.") +
            '\n\n' + '• ' + Locale.gettext("Confirm your circulation pump is turned on and running.") +
            '\n' + '• ' + Locale.gettext("Check the flow switch - a small sensor in the plumbing that detects water movement - for any visible obstructions or damage.") +
            '\n' + '• ' + Locale.gettext("If the pump is running and water is clearly flowing but this status persists, the flow switch itself may be faulty or stuck. Contact your pool professional to inspect it.") +
            '\n' + '• ' + Locale.gettext("Chemistry automation will resume automatically once water flow is confirmed."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.ERROR_CONDITION: return {
        title: Locale.gettext("Dosing Error"),
        summary: Locale.gettext("An error occurred while attempting to dispense chemicals."),
        description: Locale.gettext("Something went wrong during a recent dosing attempt and the system has stopped adding chemicals until the issue is identified.") +
            '\n\n' + '• ' + Locale.gettext("Check that your chemical tank has enough fluid - most dosing errors turn out to be an empty tank.") +
            '\n' + '• ' + Locale.gettext("Inspect the tubing running from the tank to the pool for kinks, air gaps, or blockages.") +
            '\n' + '• ' + Locale.gettext("Check the point where the chemical enters the plumbing for any crusty buildup or blockage, which can develop when the system has been idle.") +
            '\n' + '• ' + Locale.gettext("Once the root cause is addressed, open the alert on the main screen, and tap \"Try Again\" to resume dosing.") + 
            '\n' + '• ' + Locale.gettext("During the next scheduled dose, watch and listen at the chemical pump. You should hear it running and see fluid moving through the tubing. If neither happens, the pump may be clogged or have lost its prime."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.LIQUID_CHLORINE_DOSING: return {
        title: Locale.gettext("Liquid Chlorine Dosing"),
        summary: Locale.gettext("Liquid chlorine is currently being dispensed to raise sanitizer levels. Acid dosing is paused while this completes."),
        description: Locale.gettext("The system is actively adding chlorine to your water right now. Acid dosing is temporarily paused until this is complete. There is nothing for you to do at this time - this is normal operation."),
        level: ChemistryStateLevel.Yellow
    };
    case ChemistryEngineStates.ACID_DOSING: return {
        title: Locale.gettext("Acid Dosing"),
        summary: Locale.gettext("Acid is currently being dispensed to lower pH levels. Liquid chlorine dosing is paused while this completes."),
        description: Locale.gettext("The system is actively adding acid to bring your water's pH down right now. Chlorine dosing is temporarily paused until this is complete. There is nothing for you to do at this time - this is normal operation."),
        level: ChemistryStateLevel.Yellow
    };
    case ChemistryEngineStates.PROBE_DELAY: return {
        title: Locale.gettext("Waiting For Probe Stabilization"),
        summary: Locale.gettext("The system is waiting for chemistry readings to stabilize before making dosing decisions."),
        description: Locale.gettext("The system is waiting for your water readings to settle before deciding whether to add any chemicals. This happens after a dose is added or when the system first starts up. There is nothing for you to do at this time - this is normal and expected behavior."),
        level: ChemistryStateLevel.Yellow
    };
    case ChemistryEngineStates.NORMALIZED_VALUE_NULL: return {
        title: Locale.gettext("Sensor Calibration Incomplete"),
        summary: Locale.gettext("The sensor reading could not be normalized. Calibration or stabilization may still be in progress."),
        description:  Locale.gettext("The sensor is returning a reading the system isn't able to use yet. This usually means calibration hasn't been completed or the sensor is still warming up.") +
            '\n\n' + '• ' + Locale.gettext("If the sensor was recently installed or replaced, give it 10–15 minutes to stabilize before taking further action.") +
            '\n' + '• ' + Locale.gettext("If the sensor has been running for a while and this status persists, it likely needs to be calibrated. Follow the calibration steps for the sensor in Installer Mode or contact your pool professional.") +
            '\n' + '• ' + Locale.gettext("Confirm the sensor is fully in the flow of water and not exposed to air pockets in the plumbing."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.SAMPLED_AT_LEAST_ONCE: return {
        title: Locale.gettext("Water sensor has not sampled the water"),
        summary: Locale.gettext("The sensor has not been able to sample the water."),
        description: Locale.gettext("The sensor hasn't been able to take a water reading yet, so the system doesn't have enough information to make dosing decisions.") +
            '\n\n' + '• ' + Locale.gettext("Confirm the sensor is powered on.") +
            '\n' + '• ' + Locale.gettext("Confirm the sensor is physically located in the water flow path and that water is actively flowing past it.") +
            '\n' + '• ' + Locale.gettext("If the issue persists, check the sensor's wiring and connections for any visible damage or loose connections."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.SENSOR_NOT_POWERED_ON: return {
        title: Locale.gettext("Sensor Powered Off"),
        summary: Locale.gettext("The required sensor is currently powered off or unavailable."),
        description: Locale.gettext("The required sensor is off or not responding. The system cannot make any chemistry decisions without an active sensor reading.") +
            '\n\n' + '• ' + Locale.gettext("Check that the sensor is properly connected to the controller.") +
            '\n' + '• ' + Locale.gettext("Inspect the wiring between the sensor and the controller for any visible damage, loose connections, or corrosion.") +
            '\n' + '• ' + Locale.gettext("If the sensor still does not respond, contact your pool professional or Poolside Support."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.SENSOR_UNSTABLE: return {
        title: Locale.gettext("Sensor Readings Unstable"),
        summary: Locale.gettext("Sensor readings are fluctuating and chemistry adjustments are temporarily paused."),
        description: Locale.gettext("The sensor is giving inconsistent or rapidly changing readings, so the system has paused chemistry adjustments to avoid incorrect dosing.") +
            '\n\n' + '• ' + Locale.gettext("Note whether the unstable readings appear to coincide with specific equipment turning on or off - your pump, heater, light, or salt chlorine generator. If so, stray electrical interference may be the cause. Contact your pool professional to inspect the pool's electrical bonding.") +
            '\n' + '• ' + Locale.gettext("Turn off all circulation pumps. Remove the sensor from the plumbing and test it in a separate cup of pool/spa water away from the equipment pad. If it reads steadily there, the issue is likely electrical interference in the plumbing rather than the sensor itself.") +
            '\n' + '• ' + Locale.gettext("If the sensor reads erratically even out of the water, inspect its wiring for damage, corrosion, or loose connections at either end.") +
            '\n' + '• ' + Locale.gettext("Sensors that have been in service for several years may be reaching the end of their life. pH sensors typically last 1–2 years; ORP sensors typically last 2–3 years. If yours is in that range, it may need replacing."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.SENSOR_OFFLINE: return {
        title: Locale.gettext("Sensor Offline"),
        summary: Locale.gettext("The system cannot communicate with the required chemistry sensor."),
        description:  Locale.gettext("The system has lost communication with the chemistry sensor entirely. No readings are available and dosing has been paused.") +
            '\n\n' + '• ' + Locale.gettext("Check the cable connecting the sensor to the controller for any visible damage, kinks, or loose connections at either end.") +
            '\n' + '• ' + Locale.gettext("Inspect the connection points on both the sensor and the controller for corrosion or moisture.") +
            '\n' + '• ' + Locale.gettext("If you have a spare cable available, try swapping it to rule out a wiring problem before assuming the sensor itself has failed.") +
            '\n' + '• ' + Locale.gettext("If the sensor still doesn't come back online after checking wiring, contact your pool professional or Poolside Support."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.ERROR_METHOD_MISSING: return {
        title: Locale.gettext("Error Handling Not Configured"),
        summary: Locale.gettext("A required error handling or recovery method has not been configured."),
        description: Locale.gettext("The system doesn't have a recovery plan configured for when dosing errors occur. Without this, it won't know how to respond if something goes wrong during a dose cycle.") +
            '\n\n' + '• ' + Locale.gettext("Open the chemistry settings for the affected chemical in the app.") +
            '\n' + '• ' + Locale.gettext("Look for the \"Alert Detection Method\" setting and confirm it is configured. For most pools, \"Overfeed\" is the recommended choice.") +
            '\n' + '• ' + Locale.gettext("Look for the \"Resume Method\" setting and confirm it is also configured. \"Daily\" is the recommended choice for most installations, which allows the system to automatically retry the next day.") +
            '\n' + '• ' + Locale.gettext("If you're unsure how to configure these settings, contact your pool professional or Poolside Support."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.SILENT_MAXIMUM_PER_DAY_EXCEEDED: return {
        title: Locale.gettext("Daily Dosing Limit Reached"),
        summary: Locale.gettext("The maximum allowed chemical dispensing amount for today has been reached."),
        description:Locale.gettext("The system has added the maximum allowed amount of chemical for today and has stopped dosing as a safety measure. This usually means something is preventing the chemical from having its expected effect on the water.") +
            '\n\n' + '• ' + Locale.gettext("Check that the chemical tank isn't empty - an empty tank is the most common reason the system hits its daily limit without results.") +
            '\n' + '• ' + Locale.gettext("Verify the chemical pump is actually moving fluid by watching and listening during a manual dose attempt in the app.") +
            '\n' + '• ' + Locale.gettext("Inspect the tubing for kinks, blockages, or crusty buildup at the point where chemical enters the plumbing.") +
            '\n' + '• ' + Locale.gettext("If the tank and pump appear to be working fine, have the water tested manually with a home test kit to check whether the sensor reading accurately reflects your actual water conditions. A sensor that has drifted off-calibration can cause the system to dose repeatedly chasing a reading that doesn't reflect reality.") +
            '\n' + '• ' + Locale.gettext("The system will automatically retry the following day."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.NO_DOSING_TABLE_ROW_FOUND: return {
        title: Locale.gettext("Dosing Profile Missing"),
        summary: Locale.gettext("No dosing profile could be found for the current chemistry conditions."),
        description: Locale.gettext("The system tried to look up how much chemical to add based on current reading but couldn't find a matching entry in its dosing profile. Dosing has been paused.") +
            '\n\n' + '• ' + Locale.gettext("Open the chemistry dosing settings and confirm that a dosing profile row has been set up for the affected chemical.") +
            '\n' + '• ' + Locale.gettext("If the \"Use Default Procedure\" toggle is available and not already enabled, try turning it on - this automatically builds a safe dosing profile based on your pool's configuration and is the recommended setting for most pools.") +
            '\n' + '• ' + Locale.gettext("If the dosing profile appears to be in place and the issue persists, contact Poolside Support for further guidance."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.NO_FLOW_WHILE_GENERATING: return {
        title: Locale.gettext("No Flow During Generation"),
        summary: Locale.gettext("Insufficient water flow was detected while trying to operate the salt chlorine generator. Generation has been paused for safety."),
        description: Locale.gettext("The salt chlorine generator detected that water wasn't flowing properly while it was trying to run and has paused for safety. Running the generator without water flow can damage it.") +
            '\n\n' + '• ' + Locale.gettext("Confirm your circulation pump is turned on and that water is actively moving through the system.") +
            '\n' + '• ' + Locale.gettext("Check for any closed valves or blockages in the plumbing that could be restricting flow.") +
            '\n' + '• ' + Locale.gettext("If the pump is running and water is clearly flowing but this status persists, the flow switch may be faulty or stuck. Contact your pool professional to inspect it.") +
            '\n' + '• ' + Locale.gettext("Once normal water flow is restored and confirmed, the salt chlorine generator will resume operation automatically."),
        level: ChemistryStateLevel.Red
    };
    case ChemistryEngineStates.DISPENSING_DEVICE_OFFLINE: return {
        title: Locale.gettext("No connection to the dispensing device"),
        summary: Locale.gettext("The control system is unable to communicate and control your dispensing device. Water treatment has been paused until communication is restored."),
        description: Locale.gettext("In order to dispense, the device must be online and communicating with the primary Attendant.") +
            '\n\n' + '• ' + Locale.gettext("If the device is connected to a secondary (slave) Attendant please verify the secondary Attendant is powered on.") +
            '\n' + '• ' + Locale.gettext("Ensure the communication cable from the secondary Attendant to the primary Attendant is plugged in to the correct port on both the primary and secondary.") +
            '\n' + '• ' + Locale.gettext("If the device is connected to an expansion module, please verify that the expansion module is powered on and the USB cable connecting it to the master is connected."),
        level: ChemistryStateLevel.Red
    };
    default: return {
        title: state,
        summary: Locale.gettext("The system is reporting a status that is currently unknown to the app. This can happen if you have not updated your app recently."),
        description: Locale.gettext("Please try restarting the app. If that doesn't work - update the app from your app store. If the problem continues, please contact Poolside Tech support for assistance."),
        level: ChemistryStateLevel.Gray
    };
    }
}
